import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import PageHeader from '../../components/PageHeader';
import DataState from '../../components/DataState';
import StatCard from '../../components/StatCard';
import { useSession } from '../../permissions/SessionContext';
import { hasPermissions } from '../../permissions/check';
import {
  createAcademyRefundQuote,
  createAcademyRefundRequest,
  decideAcademyRefundRequest,
  fetchAcademyBillingReadiness,
  fetchAcademyRefundRequests,
  fetchAcademySiblingDiscountPolicy,
  fetchTuitionBillings,
  fetchTuitionPayments,
  previewAcademySiblingDiscount,
} from '../../api/academy';
import { academyScope } from './scope';
import { colors, layout } from '../../design-system/tokens';

const won = (n) => `${Number(n || 0).toLocaleString('ko-KR')}원`;
const STATUS_META = {
  success: { label: '성공', tone: 'success' },
  failed: { label: '실패', tone: 'danger' },
  processing: { label: '처리 중', tone: 'warning' },
  reconciliation_required: { label: '결과 확인 필요', tone: 'warning' },
};
const REFUND_STATUS = {
  approval_requested: { label: '승인 대기', tone: 'warning' },
  approved: { label: '승인 기록됨', tone: 'success' },
  rejected: { label: '반려됨', tone: 'danger' },
};
const BLOCKER_LABELS = {
  CHARGE_SWITCH_OFF: '실청구 스위치 꺼짐',
  RECURRING_NOTICE_LEGAL_NOT_APPROVED: '정기결제 고지 법무 검토 전',
  GUARDIAN_CONSENT_LEGAL_NOT_APPROVED: '법정대리인 동의 문구 검토 전',
  FIRST_MERCHANT_NOT_VERIFIED: '첫 가맹 학원 제한 검증 전',
  RECURRING_NOTICE_VERSION_MISSING: '정기결제 고지 버전 미확정',
  GUARDIAN_CONSENT_VERSION_MISSING: '법정대리인 동의 버전 미확정',
};
const SIBLING_REASON_LABELS = {
  POLICY_DISABLED: '정책 꺼짐 — 할인 0원',
  POLICY_NOT_CONFIRMED: '정책 미확정 — 할인 0원',
  MINIMUM_CHILDREN_NOT_MET: '최소 형제 수 미충족 — 할인 0원',
  POLICY_APPLIED: '확정 정책 계산 미리보기',
  ZERO_DISCOUNT: '할인 0원',
};

const fmtDateTime = (v) =>
  v
    ? new Date(v).toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';

const statusChip = (meta) => {
  const palette = colors.status[meta?.tone] ?? colors.status.neutral;
  return {
    bgcolor: palette.bg,
    color: palette.fg,
    borderColor: palette.border,
  };
};

const paymentLabel = (payment) =>
  [
    payment.studentName || '원생 미표기',
    payment.className || '반 미표기',
    payment.billingMonth || '청구월 미표기',
    won(payment.amount),
  ].join(' · ');

const paymentNextAction = (payment) => {
  if (payment.status === 'failed') {
    return '보호자에게 앱에서 결제수단과 상세 내용을 확인하도록 안내하세요. 이 화면에서 재청구하지 않습니다.';
  }
  if (payment.status === 'reconciliation_required') {
    return '중복 청구를 막기 위해 자동 재시도가 중지됐어요. 결제 결과를 운영 절차로 확인하세요.';
  }
  if (payment.status === 'processing') return '결과가 확정될 때까지 기다린 뒤 목록을 새로고침하세요.';
  return '추가 조치가 필요하지 않습니다.';
};

const billingLabel = (billing) => {
  const studentName = billing?.student?.name || '';
  return [studentName || '원생 미표기', billing.className || '반 미표기', won(billing.amount)].join(
    ' · ',
  );
};

const emptyRefundForm = {
  sourcePaymentReference: '',
  serviceStartDate: '',
  serviceEndDate: '',
  withdrawalDate: '',
  documentedDiscountAmount: '0',
  reason: '',
};

const ReadinessPanel = ({ readiness }) => {
  if (!readiness) return null;
  const checks = [
    ['실청구 스위치', readiness.chargeSwitchEnabled],
    ['정기결제 고지 법무 검토', readiness.recurringNoticeLegalApproved],
    ['법정대리인 동의 문구 검토', readiness.guardianConsentLegalApproved],
    ['첫 가맹 학원 제한 검증', readiness.firstMerchantVerified],
  ];

  return (
    <Paper sx={{ p: { xs: 2, sm: layout.cardPadding }, mb: layout.cardGap }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        sx={{ justifyContent: 'space-between', alignItems: { md: 'flex-start' } }}
      >
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
            실청구 준비 게이트
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: colors.text.secondary }}>
            모든 항목이 검증되기 전에는 Toss 카드 청구를 호출하지 않습니다.
          </Typography>
        </Box>
        <Chip
          label={readiness.ready ? '모든 게이트 충족' : '준비 모드 · 실청구 OFF'}
          sx={statusChip({ tone: readiness.ready ? 'success' : 'warning' })}
        />
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 210px), 1fr))',
          gap: 1,
          mt: 2,
        }}
      >
        {checks.map(([label, passed]) => (
          <Box
            key={label}
            sx={{
              p: 1.25,
              border: `1px solid ${colors.border.soft}`,
              borderRadius: 1.5,
              bgcolor: colors.canvas.subtle,
            }}
          >
            <Typography variant="caption" sx={{ color: colors.text.muted }}>
              {label}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.25, fontWeight: 800 }}>
              {passed ? '확인됨' : '확인 필요'}
            </Typography>
          </Box>
        ))}
      </Box>
      {(readiness.blockers ?? []).length > 0 && (
        <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 2, flexWrap: 'wrap' }}>
          {readiness.blockers.map((blocker) => (
            <Chip
              key={blocker}
              size="small"
              label={BLOCKER_LABELS[blocker] ?? '검토가 필요한 준비 항목'}
              sx={statusChip({ tone: 'warning' })}
            />
          ))}
        </Stack>
      )}
      <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: colors.text.muted }}>
        고지 버전: {readiness.recurringNoticeVersion || '미확정'} · 동의 버전:{' '}
        {readiness.guardianConsentVersion || '미확정'}
      </Typography>
    </Paper>
  );
};

const PaymentHistory = ({ rows }) => {
  const success = rows.filter((row) => row.status === 'success');
  const failed = rows.filter((row) => row.status === 'failed');
  const collected = success.reduce((sum, row) => sum + Number(row.amount || 0), 0);

  return (
    <Stack spacing={layout.cardGap}>
      <Box sx={{ display: 'flex', gap: layout.cardGap, flexWrap: 'wrap' }}>
        <StatCard label="청구 성공" value={`${success.length}건`} caption={won(collected)} />
        <StatCard
          label="청구 실패"
          value={`${failed.length}건`}
          caption={failed.length > 0 ? '확인이 필요해요' : '없음'}
        />
      </Box>
      <DataState
        empty={rows.length === 0}
        emptyText="아직 청구 이력이 없어요. 자동결제를 등록하면 매월 기록이 쌓입니다."
      >
        <Paper sx={{ p: 1 }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="학원비 청구 이력">
              <TableHead>
                <TableRow>
                  <TableCell>청구일시</TableCell>
                  <TableCell>대상 월</TableCell>
                  <TableCell>원생</TableCell>
                  <TableCell>반</TableCell>
                  <TableCell align="right">금액</TableCell>
                  <TableCell>결과</TableCell>
                  <TableCell>다음 확인</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => {
                  const meta = STATUS_META[row.status] ?? {
                    label: '상태 확인 필요',
                    tone: 'neutral',
                  };
                  return (
                    <TableRow
                      key={
                        row.reference ||
                        `${row.attemptedAt || 'unknown'}-${row.billingMonth || 'unknown'}-${index}`
                      }
                      hover
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap', color: colors.text.muted }}>
                        {fmtDateTime(row.attemptedAt)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.billingMonth || '-'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>
                        {row.studentName || '-'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.className || '-'}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {won(row.amount)}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                          <Chip size="small" label={meta.label} sx={statusChip(meta)} />
                          {row.status === 'failed' && row.error?.message && (
                            <Typography variant="caption" sx={{ color: colors.text.muted }}>
                              {row.error.message}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                          {paymentNextAction(row)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </DataState>
    </Stack>
  );
};

const RefundWorkspace = ({ payments, requests, canManage, canApproveRefund, scope, reloadRequests }) => {
  const successfulPayments = useMemo(
    () => payments.filter((row) => row.status === 'success'),
    [payments],
  );
  const [form, setForm] = useState(emptyRefundForm);
  const [quote, setQuote] = useState(null);
  const [idempotencyKey, setIdempotencyKey] = useState('');
  const [working, setWorking] = useState(false);
  const [actionId, setActionId] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const payload = () => ({
    ...scope,
    sourcePaymentReference: form.sourcePaymentReference,
    serviceStartDate: form.serviceStartDate,
    serviceEndDate: form.serviceEndDate,
    withdrawalDate: form.withdrawalDate,
    documentedDiscountAmount: Number(form.documentedDiscountAmount || 0),
  });

  const quoteReady =
    form.sourcePaymentReference &&
    form.serviceStartDate &&
    form.serviceEndDate &&
    form.withdrawalDate;

  const calculate = async () => {
    setWorking(true);
    setError('');
    setNotice('');
    try {
      const nextQuote = await createAcademyRefundQuote(payload());
      setQuote(nextQuote);
      setIdempotencyKey(`academy_refund:${form.sourcePaymentReference}:${Date.now()}`);
    } catch (err) {
      setQuote(null);
      setError(err?.response?.data?.message || '환불 견적을 계산하지 못했어요.');
    } finally {
      setWorking(false);
    }
  };

  const requestApproval = async () => {
    if (!quote || !idempotencyKey) return;
    setWorking(true);
    setError('');
    setNotice('');
    try {
      await createAcademyRefundRequest({
        ...payload(),
        idempotencyKey,
        reason: form.reason,
      });
      setNotice('환불 견적을 승인 대기 상태로 기록했어요. 실제 환불은 실행되지 않았습니다.');
      setForm(emptyRefundForm);
      setQuote(null);
      setIdempotencyKey('');
      await reloadRequests();
    } catch (err) {
      setError(err?.response?.data?.message || '환불 승인 요청을 저장하지 못했어요.');
    } finally {
      setWorking(false);
    }
  };

  const decide = async (request, decision) => {
    setActionId(request.reference);
    setError('');
    setNotice('');
    try {
      await decideAcademyRefundRequest(request.reference, decision, {
        ...scope,
        decisionNote:
          decision === 'approved'
            ? '관리자 화면에서 견적 승인 기록'
            : '관리자 화면에서 견적 반려 기록',
      });
      setNotice(
        decision === 'approved'
          ? '승인 결정을 기록했어요. 실제 Toss 환불은 실행되지 않았습니다.'
          : '반려 결정을 기록했어요.',
      );
      await reloadRequests();
    } catch (err) {
      setError(err?.response?.data?.message || '환불 요청 상태를 변경하지 못했어요.');
    } finally {
      setActionId('');
    }
  };

  return (
    <Stack spacing={layout.cardGap}>
      <Alert severity="warning">
        이 화면은 <b>계산·견적·승인 기록 전용</b>입니다. 승인해도 카드 취소나 계좌 환불은
        실행되지 않으며, 계산 기준은 법무 검토 전 임시 모델입니다. 환불 승인·반려 기록은
        master만 남길 수 있습니다.
      </Alert>
      {error && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="success">{notice}</Alert>}

      <Paper sx={{ p: { xs: 2, sm: layout.cardPadding } }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
          중도 퇴원 환불 견적
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: colors.text.secondary }}>
          성공한 원 결제와 이용 기간을 기준으로 미사용 일수의 참고 금액을 계산합니다.
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 2,
            mt: 2,
          }}
        >
          <TextField
            select
            fullWidth
            label="원 결제 선택"
            value={form.sourcePaymentReference}
            onChange={(event) => {
              setForm({ ...form, sourcePaymentReference: event.target.value });
              setQuote(null);
            }}
          >
            {successfulPayments.length === 0 && (
              <MenuItem value="" disabled>
                견적 가능한 성공 결제가 없습니다
              </MenuItem>
            )}
            {successfulPayments.map((payment) => (
              <MenuItem key={payment.reference} value={payment.reference}>
                {paymentLabel(payment)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="증빙된 할인액 (원)"
            value={form.documentedDiscountAmount}
            onChange={(event) => {
              setForm({
                ...form,
                documentedDiscountAmount: event.target.value.replace(/[^0-9]/g, ''),
              });
              setQuote(null);
            }}
            inputMode="numeric"
          />
          {[
            ['serviceStartDate', '이용 시작일'],
            ['serviceEndDate', '이용 종료일'],
            ['withdrawalDate', '퇴원일'],
          ].map(([key, label]) => (
            <TextField
              key={key}
              type="date"
              label={label}
              value={form[key]}
              onChange={(event) => {
                setForm({ ...form, [key]: event.target.value });
                setQuote(null);
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          ))}
          <TextField
            label="승인 요청 사유"
            value={form.reason}
            onChange={(event) => setForm({ ...form, reason: event.target.value.slice(0, 500) })}
            placeholder="중도 퇴원 사유와 확인한 증빙을 적어 주세요"
            multiline
            minRows={2}
          />
        </Box>
        <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
          <Button variant="contained" onClick={calculate} disabled={!quoteReady || working}>
            {working ? '처리 중…' : '환불 견적 계산'}
          </Button>
          {canManage && (
            <Button
              variant="outlined"
              onClick={requestApproval}
              disabled={!quote || working}
            >
              견적 승인 요청
            </Button>
          )}
          <Button disabled variant="outlined">
            실제 환불 실행 불가
          </Button>
        </Stack>
      </Paper>

      {quote && (
        <Paper role="region" aria-label="환불 견적 결과" sx={{ p: { xs: 2, sm: layout.cardPadding } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}
          >
            <Box>
              <Typography variant="subtitle2">환불 견적 결과</Typography>
              <Typography variant="h4" sx={{ mt: 0.5 }}>
                {won(quote.requestedRefundAmount)}
              </Typography>
            </Box>
            <Chip label="법무 검토 전 · 실행 불가" sx={statusChip({ tone: 'warning' })} />
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: 1,
            }}
          >
            {[
              ['원 결제액', won(quote.originalPaidAmount)],
              ['전체 이용일', `${quote.serviceDays}일`],
              ['사용일', `${quote.usedDays}일`],
              ['미사용일', `${quote.unusedDays}일`],
              ['기존 승인요청 반영', won(quote.priorReservedAmount)],
            ].map(([label, value]) => (
              <Box key={label}>
                <Typography variant="caption" sx={{ color: colors.text.muted }}>
                  {label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      <Paper sx={{ p: { xs: 2, sm: layout.cardPadding } }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1.5 }}>
          환불 승인 요청
        </Typography>
        {requests.length === 0 ? (
          <Typography variant="body2" sx={{ color: colors.text.muted }}>
            아직 환불 승인 요청이 없습니다.
          </Typography>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="환불 승인 요청 목록">
              <TableHead>
                <TableRow>
                  <TableCell>요청일</TableCell>
                  <TableCell>상태</TableCell>
                  <TableCell align="right">견적 금액</TableCell>
                  <TableCell>사유</TableCell>
                  <TableCell align="right">관리</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((request) => {
                  const meta = REFUND_STATUS[request.status] ?? {
                    label: '상태 확인 필요',
                    tone: 'neutral',
                  };
                  const pending = request.status === 'approval_requested';
                  return (
                    <TableRow key={request.reference} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {fmtDateTime(request.requestedAt)}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={meta.label} sx={statusChip(meta)} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>
                        {won(request.requestedRefundAmount)}
                      </TableCell>
                      <TableCell>{request.reason || '-'}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {canApproveRefund && pending ? (
                          <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end' }}>
                            <Button
                              size="small"
                              onClick={() => decide(request, 'approved')}
                              disabled={actionId === request.reference}
                            >
                              승인 기록
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => decide(request, 'rejected')}
                              disabled={actionId === request.reference}
                            >
                              반려 기록
                            </Button>
                          </Stack>
                        ) : (
                          <Typography variant="caption" sx={{ color: colors.text.muted }}>
                            {pending
                              ? '승인·반려 기록은 master만 남길 수 있어요'
                              : '실제 환불 없음'}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Stack>
  );
};

const SiblingDiscountPanel = ({ policy, billings, scope }) => {
  const [billingReference, setBillingReference] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const runPreview = async () => {
    setLoading(true);
    setError('');
    try {
      setPreview(
        await previewAcademySiblingDiscount({ ...scope, billingReference }),
      );
    } catch (err) {
      setPreview(null);
      setError(err?.response?.data?.message || '형제 할인 미리보기를 계산하지 못했어요.');
    } finally {
      setLoading(false);
    }
  };

  const confirmed = policy?.status === 'confirmed';
  const policyType = policy?.discountType === 'fixed' ? '정액 할인' : '정률 할인';

  return (
    <Stack spacing={layout.cardGap}>
      <Alert severity="info">
        형제 할인은 현재 <b>청구 금액에 자동 반영되지 않습니다.</b> 정책이 미확정이면 항상
        0원 할인으로 계산합니다.
      </Alert>
      <Paper sx={{ p: { xs: 2, sm: layout.cardPadding } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
              형제 할인 정책 상태
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: colors.text.secondary }}>
              {policyType} · 최소 {policy?.minimumChildren ?? 2}명 · 정책 버전{' '}
              {policy?.version || '미확정'}
            </Typography>
          </Box>
          <Chip
            label={policy?.enabled && confirmed ? '정책 활성' : 'OFF · 금액 영향 0원'}
            sx={statusChip({ tone: policy?.enabled && confirmed ? 'success' : 'neutral' })}
          />
        </Stack>
        <Typography variant="body2" sx={{ mt: 2 }}>
          설정값:{' '}
          {policy?.discountType === 'fixed'
            ? won(policy?.discountValue)
            : `${policy?.discountValue ?? 0}%`}
        </Typography>
        {!confirmed && (
          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: colors.text.muted }}>
            정책 수치와 적용 조건은 운영·법무 확정 후 별도 승인 절차에서 활성화합니다.
          </Typography>
        )}
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: layout.cardPadding } }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
          자동결제별 계산 미리보기
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
          <TextField
            select
            fullWidth
            label="자동결제 등록 선택"
            value={billingReference}
            onChange={(event) => {
              setBillingReference(event.target.value);
              setPreview(null);
            }}
          >
            {billings.length === 0 && (
              <MenuItem value="" disabled>
                등록된 자동결제가 없습니다
              </MenuItem>
            )}
            {billings.map((billing) => (
              <MenuItem key={billing.reference} value={billing.reference}>
                {billingLabel(billing)}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            onClick={runPreview}
            disabled={!billingReference || loading}
            sx={{ flexShrink: 0 }}
          >
            {loading ? '계산 중…' : '할인 미리보기'}
          </Button>
        </Stack>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {preview && (
          <Box role="region" aria-label="형제 할인 미리보기 결과" sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: 1,
              }}
            >
              {[
                ['적용 대상 자녀', `${preview.eligibleChildren}명`],
                ['기준 금액', won(preview.baseAmount)],
                ['계산 할인액', won(preview.discountAmount)],
                ['계산 후 금액', won(preview.finalAmount)],
              ].map(([label, value]) => (
                <Box key={label}>
                  <Typography variant="caption" sx={{ color: colors.text.muted }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Alert severity={preview.affectsCharge ? 'warning' : 'info'} sx={{ mt: 2 }}>
              {SIBLING_REASON_LABELS[preview.reason] ?? '정책 상태를 확인해 주세요.'} · 실제 청구
              반영 {preview.affectsCharge ? '예정' : '안 함'}
            </Alert>
          </Box>
        )}
      </Paper>
    </Stack>
  );
};

/**
 * 학원 결제 운영 화면.
 * 환불과 형제 할인은 계산/승인 준비만 제공하며 실제 Toss 실행 경로는 만들지 않는다.
 */
const AcademyPayments = () => {
  const { session } = useSession();
  const canManage = hasPermissions(session, ['tuition.manage']);
  const canApproveRefund = canManage && session?.user?.role === 'master';
  const scope = useMemo(() => academyScope(session), [session]);
  const [tab, setTab] = useState('history');
  const [payments, setPayments] = useState([]);
  const [billings, setBillings] = useState([]);
  const [requests, setRequests] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [paymentRows, billingRes, readinessRes, refundRows, policyRes] = await Promise.all([
        fetchTuitionPayments(scope),
        fetchTuitionBillings(scope),
        fetchAcademyBillingReadiness(),
        fetchAcademyRefundRequests(scope),
        fetchAcademySiblingDiscountPolicy(scope),
      ]);
      setPayments(Array.isArray(paymentRows) ? paymentRows : []);
      setBillings(Array.isArray(billingRes?.items) ? billingRes.items : []);
      setReadiness(
        readinessRes ?? {
          ready: Boolean(billingRes?.chargeEnabled),
          chargeSwitchEnabled: Boolean(billingRes?.chargeEnabled),
          recurringNoticeLegalApproved: false,
          guardianConsentLegalApproved: false,
          firstMerchantVerified: false,
          recurringNoticeVersion: 'UNREVIEWED',
          guardianConsentVersion: 'UNREVIEWED',
          blockers: billingRes?.chargeEnabled ? [] : ['CHARGE_SWITCH_OFF'],
        },
      );
      setRequests(Array.isArray(refundRows) ? refundRows : []);
      setPolicy(policyRes);
    } catch (err) {
      setError(err?.response?.data?.message || '학원 결제 준비 정보를 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, [scope]);

  const reloadRequests = useCallback(async () => {
    const rows = await fetchAcademyRefundRequests(scope);
    setRequests(Array.isArray(rows) ? rows : []);
  }, [scope]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box>
      <PageHeader
        title="결제 관리"
        description="학원비 청구 이력과 중도 퇴원 환불 견적, 형제 할인 준비 상태를 관리해요."
      />

      <DataState
        loading={loading}
        error={error}
        onRetry={load}
        skeleton={{ rows: 5, cols: 5 }}
      >
        <ReadinessPanel readiness={readiness} />
        <Paper sx={{ mb: layout.cardGap }}>
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="학원 결제 관리 메뉴"
          >
            <Tab value="history" label="청구 이력" />
            <Tab value="refund" label="환불 견적·승인" />
            <Tab value="sibling" label="형제 할인 준비" />
          </Tabs>
        </Paper>

        {tab === 'history' && <PaymentHistory rows={payments} />}
        {tab === 'refund' && (
          <RefundWorkspace
            payments={payments}
            requests={requests}
            canManage={canManage}
            canApproveRefund={canApproveRefund}
            scope={scope}
            reloadRequests={reloadRequests}
          />
        )}
        {tab === 'sibling' && (
          <SiblingDiscountPanel policy={policy} billings={billings} scope={scope} />
        )}
      </DataState>
    </Box>
  );
};

export default AcademyPayments;
